import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useBluelagoon } from "@bluelagoon";
import { Card } from "@shadcn-in-nx/ui/card";
import { Badge } from "@shadcn-in-nx/ui/badge";
import { Loader2 } from "lucide-react";
import AIPerformanceChart from "./AIPerformanceChart";
import AIPerformanceStats from "./AIPerformanceStats";

export default function AIPerformance() {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);
    const { call: fetchPerformance, isLoading } = useBluelagoon({ method: "getAIPerformance" });

    const load = useCallback(async () => {
        const res = await fetchPerformance({ days }).catch(() => null);
        if (res?.data) setData(res.data);
    }, [fetchPerformance, days]);

    useEffect(() => { load(); }, [load]);

    if (isLoading && !data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">{t("admin.support.aiPerformance", "AI Performance")}</h2>
                <div className="flex gap-1">
                    {[7, 30, 90].map((d) => (
                        <button key={d} onClick={() => setDays(d)}
                            className={`px-3 py-1 text-xs rounded-full ${days === d ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`}>
                            {d}d
                        </button>
                    ))}
                </div>
            </div>
            {data?.totals && <AIPerformanceStats totals={data.totals} byVersion={data.byVersion} />}
            {data?.daily?.length > 0 ? (
                <Card className="p-4">
                    <AIPerformanceChart daily={data.daily} promptChanges={data.promptChanges} />
                </Card>
            ) : (
                <Card className="p-8 text-center">
                    <p className="text-muted-foreground">{t("admin.support.noPerformanceData", "No AI reply data yet")}</p>
                </Card>
            )}
            {data?.byVersion?.length > 1 && (
                <Card className="p-4">
                    <h3 className="text-sm font-semibold mb-3">{t("admin.support.byPromptVersion", "By Prompt Version")}</h3>
                    <div className="space-y-2">
                        {data.byVersion.map((v) => {
                            const rate = v.total > 0 ? Math.round((v.accepted / v.total) * 100) : 0;
                            return (
                                <div key={v.version} className="flex items-center gap-3 text-sm">
                                    <Badge variant="outline">v{v.version}</Badge>
                                    <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
                                        <div className="bg-green-500 h-full" style={{ width: `${rate}%` }} />
                                    </div>
                                    <span className="text-xs w-20 text-right">{rate}% ({v.accepted}/{v.total})</span>
                                </div>
                            );
                        })}
                    </div>
                </Card>
            )}
        </div>
    );
}
